import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { useInvoices, useCompany } from '../hooks/useApi';
import { formatCurrency, formatDate, getStatusBadgeClass, storage, todayYMD, addDaysYMD } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Download, 
  MoreVertical,
  Send,
  CheckCircle,
  XCircle,
  Loader2,
  Copy
} from 'lucide-react';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getInvoice, updateStatus, downloadPdf, loading } = useInvoices();
  const { getCompany } = useCompany();
  
  const [invoice, setInvoice] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const [invoiceRes, companyRes] = await Promise.all([
        getInvoice(id),
        getCompany()
      ]);
      
      if (invoiceRes.data) setInvoice(invoiceRes.data);
      if (companyRes.data) setCompany(companyRes.data);
    };
    loadData();
  }, [id]);

  const handleStatusChange = async (status) => {
    const { error } = await updateStatus(id, status);
    if (!error) {
      toast.success(`Invoice marked as ${status}`);
      setInvoice(prev => ({ ...prev, status }));
    }
  };

  const handleDownload = () => {
    if (invoice) {
      downloadPdf(id, invoice.invoice_number);
    }
  };

  const handleDuplicate = () => {
    if (!invoice) return;
    
    // Create draft from invoice data
    const draft = {
      customer_id: invoice.customer_id || '',
      customer_name: invoice.customer_name || '',
      customer_address: invoice.customer_address || '',
      customer_city: invoice.customer_city || '',
      customer_postal_code: invoice.customer_postal_code || '',
      customer_country: invoice.customer_country || '',
      customer_tax_id: invoice.customer_tax_id || '',
      line_items: invoice.line_items || [{ description: '', quantity: 1, unit_price: 0, unit: 'pcs' }],
      tax_rate: invoice.tax_rate || 0,
      currency: invoice.currency || 'USD',
      issue_date: invoice.issue_date || todayYMD(),
      due_date: invoice.due_date || addDaysYMD(todayYMD(), 30),
      footer_notes: invoice.footer_notes || '',
      payment_conditions: invoice.payment_conditions || ''
    };

    // Save to localStorage
    storage.set('invoiceflow_draft', draft);
    toast.success('Invoice copied to draft');
    navigate('/invoices/new');
  };

  if (loading || !invoice) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/invoices')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight font-mono">
                #{invoice.invoice_number}
              </h1>
              <Badge 
                variant="outline"
                className={getStatusBadgeClass(invoice.status)}
              >
                {invoice.status}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              {invoice.customer_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            className="gap-2"
            onClick={handleDuplicate}
            data-testid="duplicate-invoice-btn"
          >
            <Copy className="w-4 h-4" />
            Duplicate
          </Button>
          <Button 
            variant="outline"
            className="gap-2"
            onClick={handleDownload}
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="invoice-actions-menu">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {invoice.status === 'created' && (
                <DropdownMenuItem onClick={() => handleStatusChange('sent')}>
                  <Send className="w-4 h-4 mr-2" />
                  Mark as Sent
                </DropdownMenuItem>
              )}
              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                <DropdownMenuItem onClick={() => handleStatusChange('paid')}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Paid
                </DropdownMenuItem>
              )}
              {invoice.status !== 'cancelled' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleStatusChange('cancelled')}>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Invoice
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Invoice Preview */}
      <div className="pdf-preview-container">
        <Card className="invoice-preview bg-white max-w-[800px] w-full">
          <CardContent className="p-8 md:p-12">
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-10">
              <div className="flex items-start gap-4">
                {company?.logo_base64 && (
                  <img 
                    src={company.logo_base64} 
                    alt="Company Logo" 
                    className="w-16 h-16 object-contain"
                  />
                )}
                <div>
                  {company?.name && (
                    <h2 className="text-xl font-bold text-slate-900">{company.name}</h2>
                  )}
                  {company?.address && (
                    <p className="text-sm text-slate-600">{company.address}</p>
                  )}
                  {(company?.city || company?.postal_code) && (
                    <p className="text-sm text-slate-600">
                      {company.city} {company.postal_code}
                    </p>
                  )}
                {company?.country && (
                  <p className="text-sm text-slate-600">{company.country}</p>
                )}
                {company?.tax_id && (
                  <p className="text-sm text-slate-600">Tax ID: {company.tax_id}</p>
                )}
                {company?.email && (
                  <p className="text-sm text-slate-600">{company.email}</p>
                )}
                {company?.phone && (
                  <p className="text-sm text-slate-600">{company.phone}</p>
                )}
                </div>
              </div>
              <div className="text-right">
                <h1 className="text-3xl font-extrabold text-slate-900">INVOICE</h1>
                <p className="font-mono text-xl text-accent font-bold">
                  #{invoice.invoice_number}
                </p>
                <div className="mt-4 space-y-1">
                  <p className="text-sm">
                    <span className="text-slate-500">Issue Date: </span>
                    <span className="text-slate-900">{formatDate(invoice.issue_date)}</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-slate-500">Due Date: </span>
                    <span className="text-slate-900">{formatDate(invoice.due_date)}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div className="mb-8">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Bill To
              </h3>
              <p className="font-semibold text-slate-900">{invoice.customer_name}</p>
              {invoice.customer_address && (
                <p className="text-sm text-slate-600">{invoice.customer_address}</p>
              )}
              {(invoice.customer_city || invoice.customer_postal_code) && (
                <p className="text-sm text-slate-600">
                  {invoice.customer_city} {invoice.customer_postal_code}
                </p>
              )}
              {invoice.customer_country && (
                <p className="text-sm text-slate-600">{invoice.customer_country}</p>
              )}
              {invoice.customer_tax_id && (
                <p className="text-sm text-slate-600">Tax ID: {invoice.customer_tax_id}</p>
              )}
            </div>

            {/* Line Items Table */}
            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3">
                      Description
                    </th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3">
                      Qty
                    </th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3">
                      Unit
                    </th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3">
                      Price
                    </th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((item, index) => (
                    <tr key={index} className="border-b border-slate-100">
                      <td className="py-4 text-sm text-slate-900">{item.description}</td>
                      <td className="py-4 text-sm text-right font-mono text-slate-700">
                        {item.quantity}
                      </td>
                      <td className="py-4 text-sm text-right text-slate-700">
                        {item.unit}
                      </td>
                      <td className="py-4 text-sm text-right font-mono text-slate-700">
                        {formatCurrency(item.unit_price, invoice.currency)}
                      </td>
                      <td className="py-4 text-sm text-right font-mono font-semibold text-slate-900">
                        {formatCurrency(item.quantity * item.unit_price, invoice.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm py-1">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-mono text-slate-900">
                    {formatCurrency(invoice.subtotal, invoice.currency)}
                  </span>
                </div>
                {invoice.tax_rate > 0 && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-slate-600">Tax ({invoice.tax_rate}%)</span>
                    <span className="font-mono text-slate-900">
                      {formatCurrency(invoice.tax_amount, invoice.currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg py-3 border-t-2 border-slate-900">
                  <span>Total</span>
                  <span className="font-mono">
                    {formatCurrency(invoice.total, invoice.currency)}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            {(invoice.payment_conditions || invoice.footer_notes) && (
              <div className="border-t border-slate-200 pt-6 space-y-4 text-sm">
                {invoice.payment_conditions && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Payment Terms</h4>
                    <p className="text-slate-600">{invoice.payment_conditions}</p>
                  </div>
                )}
                {invoice.footer_notes && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Notes</h4>
                    <p className="text-slate-600 whitespace-pre-wrap">{invoice.footer_notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
